import { useEffect, useRef, useState } from "react";
import { ScanFace, Mic, Play, Pause } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { CAPTION_STYLES, FRAMINGS, HOOK_STYLES, PACINGS, BACKEND_URL } from "@/lib/api";

const Select = ({ label, value, options, onChange, testid }) => (
  <label className="space-y-1 text-sm">
    <span className="overline-label">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-10 rounded-xl border border-input bg-background px-3 text-sm" data-testid={testid}>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </label>
);

export const VariationEditDialog = ({ variation, maxDuration, voices = [], onClose, onSave }) => {
  const [form, setForm] = useState(null);
  const [voEnabled, setVoEnabled] = useState(false);
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);
  useEffect(() => {
    if (variation) {
      setForm({ hook: variation.hook || "", caption: variation.caption || "", hook_style: variation.plan.hook_style, caption_style: variation.plan.caption_style, framing: variation.plan.framing, pacing: variation.plan.pacing, start: variation.plan.start, duration: variation.plan.duration, track_faces: !!variation.plan.track_faces, voiceover_text: variation.voiceover?.text || "", voiceover_voice: variation.voiceover?.voice || voices[0]?.id || "alloy" });
      setVoEnabled(!!variation.voiceover);
    }
  }, [variation, voices]);
  useEffect(() => () => audioRef.current?.pause(), []);
  if (!variation || !form) return null;
  const set = (k) => (v) => setForm({ ...form, [k]: v });
  const previewVoice = (id) => {
    const a = audioRef.current;
    if (!a) return;
    if (playing === id) { a.pause(); setPlaying(null); return; }
    a.src = `${BACKEND_URL}/api/tts/voices/${id}/preview`;
    setPlaying(id);
    a.play().catch((e) => { if (e?.name !== "AbortError") setPlaying(null); });
  };
  const hadVo = !!variation.voiceover;
  const voChanged = voEnabled
    ? (!hadVo || variation.voiceover.text !== form.voiceover_text.trim() || variation.voiceover.voice !== form.voiceover_voice)
    : hadVo;
  const planChanged = ["hook", "hook_style", "caption_style", "framing", "pacing", "start", "duration", "track_faces"].some((k) => String(form[k]) !== String(k === "hook" ? variation.hook : k === "track_faces" ? !!variation.plan.track_faces : variation.plan[k]));
  const rerenderNeeded = planChanged || voChanged;
  const buildBody = () => {
    const body = { ...form, rerender: rerenderNeeded };
    if (voEnabled && form.voiceover_text.trim()) { body.voiceover_text = form.voiceover_text.trim(); body.voiceover_voice = form.voiceover_voice; }
    else { body.clear_voiceover = hadVo; delete body.voiceover_text; delete body.voiceover_voice; }
    return body;
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="variation-edit-dialog">
        <audio ref={audioRef} onEnded={() => setPlaying(null)} className="hidden" />
        <DialogHeader><DialogTitle className="font-display tracking-tight">Edit variation #{variation.index + 1}</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Edit this variation's hook, caption, framing, pacing and optional AI voiceover.</DialogDescription>
        <div className="space-y-4">
          <label className="space-y-1 block"><span className="overline-label">On-screen hook</span><Input value={form.hook} onChange={(e) => set("hook")(e.target.value)} className="rounded-xl" data-testid="edit-hook-input" /></label>
          <label className="space-y-1 block"><span className="overline-label">Post caption</span><Textarea value={form.caption} onChange={(e) => set("caption")(e.target.value)} rows={3} className="rounded-xl" data-testid="edit-caption-input" /></label>
          <div className="grid grid-cols-2 gap-3">
            <Select label="Hook style" value={form.hook_style} options={HOOK_STYLES} onChange={set("hook_style")} testid="edit-hook-style" />
            <Select label="Caption style" value={form.caption_style} options={CAPTION_STYLES} onChange={set("caption_style")} testid="edit-caption-style" />
            <Select label="Framing" value={form.framing} options={FRAMINGS} onChange={set("framing")} testid="edit-framing" />
            <Select label="Pacing" value={form.pacing} options={PACINGS} onChange={set("pacing")} testid="edit-pacing" />
            <label className="space-y-1 text-sm"><span className="overline-label">Start (s)</span><Input type="number" step="0.5" min={0} max={maxDuration - 3} value={form.start} onChange={(e) => set("start")(parseFloat(e.target.value))} className="rounded-xl" data-testid="edit-start-input" /></label>
            <label className="space-y-1 text-sm"><span className="overline-label">Duration (s)</span><Input type="number" step="1" min={3} max={60} value={form.duration} onChange={(e) => set("duration")(parseFloat(e.target.value))} className="rounded-xl" data-testid="edit-duration-input" /></label>
          </div>
          <label className="flex items-center justify-between rounded-xl border border-border p-3 text-sm">
            <span className="flex items-center gap-2"><ScanFace size={14} className="text-primary" /> Follow faces (smart framing)</span>
            <Switch checked={form.track_faces} onCheckedChange={set("track_faces")} data-testid="edit-track-faces-switch" />
          </label>
          <div className="rounded-xl border border-border p-3 space-y-3">
            <label className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2"><Mic size={14} className="text-primary" /> AI voiceover</span>
              <Switch checked={voEnabled} onCheckedChange={setVoEnabled} data-testid="edit-voiceover-switch" />
            </label>
            {voEnabled && (
              <div className="space-y-2">
                <Textarea value={form.voiceover_text} maxLength={600} onChange={(e) => set("voiceover_text")(e.target.value)} rows={2} placeholder="Type what the voice says — it's mixed over the clip, ducking the audio." className="rounded-xl text-sm" data-testid="edit-voiceover-text" />
                <div className="flex flex-wrap gap-1.5" data-testid="edit-voiceover-voices">
                  {voices.map((v) => (
                    <div key={v.id} className={`flex items-center gap-1 rounded-full border pl-1 pr-2 py-1 text-xs ${form.voiceover_voice === v.id ? "border-primary bg-primary/10" : "border-border"}`}>
                      <button type="button" onClick={() => previewVoice(v.id)} className="h-6 w-6 rounded-full bg-primary/15 text-primary grid place-items-center" data-testid={`edit-voiceover-preview-${v.id}`} aria-label={`Preview ${v.label}`}>{playing === v.id ? <Pause size={11} /> : <Play size={11} className="ml-0.5" />}</button>
                      <button type="button" onClick={() => set("voiceover_voice")(v.id)} className="font-medium" data-testid={`edit-voiceover-voice-${v.id}`}>{v.label}</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground">{rerenderNeeded ? "Changes to the clip, overlays or voiceover trigger a re-render (1 credit)." : "Caption-only edits save instantly without re-rendering."}</p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} className="btn-pill" data-testid="edit-cancel-button">Cancel</Button>
            <Button onClick={() => onSave(buildBody())} className="btn-pill" data-testid="edit-save-button">{rerenderNeeded ? "Save & re-render" : "Save"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
